import HomePage from '@pages/Home/HomePage';
import { NotFound } from '@pages/NotFound';
import RootPage from '@pages/RootPage';
import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router';

const asyncComponent = (importFunc: () => Promise<{ default: React.ComponentType }>) => {
  const Component = lazy(importFunc);
  return (
    <Suspense fallback={<div />}>
      <Component />
    </Suspense>
  );
};

const routes: RouteObject[] = [
  {
    path: '/login/*',
    element: asyncComponent(() => import('@pages/Auth/LoginPage')),
  },
  {
    path: '/register/*',
    element: asyncComponent(() => import('@pages/Auth/RegisterPage')),
  },
  {
    path: '/permissionDenied',
    element: asyncComponent(() => import('@pages/PermissionPage')),
  },
  {
    path: '/',
    element: <RootPage />,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: <HomePage />,
        handle: { navigator: true, requireMenu: true },
      },
      {
        path: 'article/:id',
        element: asyncComponent(() => import('@pages/Article/ArticlePage')),
        handle: { navigator: false, requireMenu: true },
      },
    ],
  },
  {
    path: 'question/',
    element: <RootPage />,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/Question/QuestionSelectPage')),
        handle: { navigator: true, breadcrumb: 'Question' },
      },
      {
        path: 'record/:id',
        element: asyncComponent(() => import('@pages/Question/RecordPage')),
        handle: { navigator: false, breadcrumb: 'RecordPage' },
      },
      {
        path: ':id',
        element: asyncComponent(() => import('@pages/Question/QuestionPage')),
        handle: { navigator: true, breadcrumb: 'Question' },
      },
      {
        path: ':id/connection/:connectionId',
        element: asyncComponent(() => import('@pages/Question/QuestionPage')),
        handle: { navigator: false, breadcrumb: 'SharedQuestion' },
      },
    ],
  },
  {
    path: 'more/',
    element: <RootPage />,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/More/MorePage')),
        handle: { navigator: true, breadcrumb: 'More' },
      },
      {
        path: 'about',
        element: asyncComponent(() => import('@pages/More/AboutPage')),
        handle: { navigator: false, breadcrumb: 'About' },
      },
      {
        path: 'contact',
        element: asyncComponent(() => import('@pages/More/ContactPage')),
        handle: { navigator: false, breadcrumb: 'Contact' },
      },
      {
        path: 'updatePassword',
        element: asyncComponent(() => import('@pages/More/PasswordEdit')),
        handle: { navigator: false, breadcrumb: 'PasswordEdit' },
      },
    ],
  },
  {
    path: 'connection/',
    element: <RootPage />,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/Connection/ConnectionRelationshipPage')),
        handle: { navigator: true },
      },
      {
        path: 'edit',
        element: asyncComponent(() => import('@pages/Connection/ConnectionRelationshipEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/relationship/edit',
        element: asyncComponent(() => import('@pages/Connection/RelationshipEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/relationship/edit/:rid',
        element: asyncComponent(() => import('@pages/Connection/RelationshipEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/experience/edit',
        element: asyncComponent(() => import('@pages/Connection/ExperienceEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/experience/edit/:eid',
        element: asyncComponent(() => import('@pages/Connection/ExperienceEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/education/edit',
        element: asyncComponent(() => import('@pages/Connection/EducationEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/education/edit/:eid',
        element: asyncComponent(() => import('@pages/Connection/EducationEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/certificate/edit',
        element: asyncComponent(() => import('@pages/Connection/CertificateEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/certificate/edit/:cid',
        element: asyncComponent(() => import('@pages/Connection/CertificateEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id/edit/',
        element: asyncComponent(() => import('@pages/Connection/ConnectionRelationshipEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id',
        element: asyncComponent(() => import('@pages/Connection/ConnectionRelationshipDetail')),
        handle: { navigator: false, breadcrumb: 'Detail' },
      },
    ],
  },
  {
    path: 'discussion/',
    element: <RootPage />,
    errorElement: <NotFound />,
    handle: { requiresAuth: true },
    children: [
      {
        index: true,
        element: asyncComponent(() => import('@pages/Discussion/DiscussionPage')),
        handle: { navigator: true, breadcrumb: 'Discussion' },
      },
      {
        path: 'edit/:cid',
        element: asyncComponent(() => import('@pages/Discussion/DiscussionEdit')),
        handle: { navigator: false, breadcrumb: 'Add' },
      },
      {
        path: ':id/edit/:cid',
        element: asyncComponent(() => import('@pages/Discussion/DiscussionEdit')),
        handle: { navigator: false },
      },
      {
        path: ':id',
        element: asyncComponent(() => import('@pages/Discussion/DiscussionDetail')),
        handle: { navigator: false, breadcrumb: 'Detail' },
      },
    ],
  },
  {
    path: 'admin/',
    element: <RootPage />,
    errorElement: <NotFound />,
    handle: { requiresAuth: true, adminOnly: true },
    children: [
      {
        path: 'dashboard',
        element: asyncComponent(() => import('@pages/Admin/Dashboard/DashboardPage')),
        handle: { navigator: true, breadcrumb: 'User Management' },
      },
      {
        path: 'user',
        element: asyncComponent(() => import('@pages/Admin/User/UserPage')),
        handle: { navigator: true, breadcrumb: 'User Management' },
      },
      {
        path: 'content',
        element: asyncComponent(() => import('@pages/Admin/Content/ContentPage')),
        handle: { navigator: true, breadcrumb: 'Content Management' },
      },
      {
        path: 'permission',
        element: asyncComponent(() => import('@pages/Admin/Permission/PermissionManagePage')),
        handle: { navigator: true, breadcrumb: 'Permission Management' },
      },
      {
        path: 'discussion',
        element: asyncComponent(() => import('@pages/Admin/DiscussionManage/DiscussionManagePage')),
        handle: { navigator: true, breadcrumb: 'Discussion Management' },
      },
      {
        path: 'question',
        element: asyncComponent(() => import('@pages/Admin/QuestionManage/QuestionManagePage')),
        handle: { navigator: true, breadcrumb: 'Question Management' },
      },
    ],
  },
  {
    path: 'notFound/',
    element: <NotFound />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
